import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { RotateCw, Loader2, Check, ImageIcon, ArrowLeft, Sparkles, Layers, ChevronRight, Award, Baby, GraduationCap, FolderTree, FileText, Lock, RefreshCw, Plus } from "lucide-react";
import CoachToolModal from "@/components/CoachToolModal";
import TermDeck from "@/components/TermDeck";

const BADGE_STYLES = {
  bronze: { ring: "#c58a4a", fill: "#f0dcbd", label: "Bronze" },
  silver: { ring: "#a3a8a5", fill: "#e2e5e2", label: "Silver" },
  gold:   { ring: "#d4a537", fill: "#f6e6a8", label: "Gold" },
};

function BadgeMedal({ tier, size = 26, tooltip }) {
  if (!tier || !BADGE_STYLES[tier]) return null;
  const s = BADGE_STYLES[tier];
  const label = tooltip || `${s.label} mastery`;
  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0"
      style={{
        width: size, height: size,
        background: s.fill,
        border: `2px solid ${s.ring}`,
      }}
      title={label}
      aria-label={label}
      data-testid={`badge-medal-${tier}`}
    >
      <Award size={Math.round(size * 0.55)} style={{ color: s.ring }} />
    </div>
  );
}

function FlashcardGenerator({ card, onFilled, onError }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fill = async () => {
    if (!card || !card.topic_id) {
      setError({ isPaywall: false, message: "Card is missing an ID — head back to the picker and re-open the deck." });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await api.post(`/topics/${card.topic_id}/generate-content`);
      onFilled(r.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const isPaywall = status === 402;
      setError({
        isPaywall,
        message: isPaywall
          ? (typeof detail === "object" && detail?.reason ? detail.reason : "You're out of AI credits. Upgrade or buy a top-up pack to keep generating flashcards.")
          : (typeof detail === "string" ? detail : "Generation hiccupped. Try again in a moment."),
      });
      if (onError) onError({ isPaywall, status });
    } finally { setLoading(false); }
  };
  useEffect(() => { if (!loading && !error) fill(); }, []);   // eslint-disable-line
  if (error) {
    return (
      <div className="text-center p-6" data-testid="flash-error">
        {error.isPaywall ? (
          <>
            <Lock size={28} className="mx-auto mb-3" style={{ color: "#c98a3b" }} />
            <p className="font-display font-bold text-lg mb-2">Out of credits</p>
            <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>{error.message}</p>
            <div className="flex gap-2 justify-center flex-wrap">
              <a href="/app/plans" className="pm-btn-primary inline-flex items-center gap-2">Upgrade</a>
              <a href="/app/plans#credit-packs" className="pm-badge" style={{ background: "var(--muted)", padding: "8px 14px" }}>Buy credits</a>
            </div>
          </>
        ) : (
          <>
            <p className="font-display font-bold text-lg mb-2">Couldn't paint that flashcard</p>
            <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>{error.message}</p>
            <button onClick={fill} className="pm-btn-primary inline-flex items-center gap-2" data-testid="flash-retry-btn">
              <RefreshCw size={14} /> Try again
            </button>
          </>
        )}
      </div>
    );
  }
  return (
    <div className="text-center" data-testid="flash-generating">
      <Loader2 size={32} className="animate-spin mx-auto mb-4" style={{ color: "var(--primary)" }} />
      <p className="font-display font-bold text-lg mb-1">Painting your flashcard…</p>
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
        Cooking up the analogy, story, key facts &amp; illustration for <b>{card.name}</b>.
      </p>
      <p className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>This takes about 10–20 seconds.</p>
    </div>
  );
}

const RATINGS = [
  { id: "again", label: "Again", desc: "< 1d", color: "var(--weakness)" },
  { id: "hard", label: "Hard", desc: "2d", color: "#c98a3b" },
  { id: "good", label: "Good", desc: "4d", color: "var(--primary)" },
  { id: "easy", label: "Easy", desc: "8d", color: "var(--mastery)" },
];

const SUBJECT_ACCENTS = {
  biology: "#4f7942",
  biochemistry: "#7ba55c",
  "gen-chem": "#c98a3b",
  orgo: "#e88d67",
  physics: "#4f6e94",
  psych: "#8b6ea0",
  socio: "#8a5a76",
  cars: "#5e6460",
};

// --- Picker view components -----------------------------------------------

function PickerLanding({ catalog, onPickSmart, onPickSubject }) {
  const totalGold = catalog.reduce((n, s) => n + s.categories.filter((c) => c.badge === "gold").length, 0);
  const totalSilver = catalog.reduce((n, s) => n + s.categories.filter((c) => c.badge === "silver").length, 0);
  const totalBronze = catalog.reduce((n, s) => n + s.categories.filter((c) => c.badge === "bronze").length, 0);
  const anyBadges = totalGold + totalSilver + totalBronze > 0;
  return (
    <div data-testid="flash-picker-landing">
      <h1 className="font-display text-4xl font-bold mb-2">Flashcards</h1>
      <p className="text-lg mb-4" style={{ color: "var(--text-secondary)" }}>
        Study a mixed smart deck, or browse by subject and category.
      </p>

      {/* Mastery badge legend / count */}
      <div
        className="pm-card p-3 mb-6 flex items-center justify-between flex-wrap gap-3"
        data-testid="flash-badges-summary"
      >
        <div className="flex items-center gap-3 text-xs" style={{ color: "var(--text-secondary)" }}>
          <div className="flex items-center gap-1.5"><BadgeMedal tier="bronze" size={18} tooltip="Bronze: 60%+ over 5 attempts" /><span>60% · 5+ attempts</span></div>
          <div className="flex items-center gap-1.5"><BadgeMedal tier="silver" size={18} tooltip="Silver: 75%+ over 5 attempts" /><span>75%</span></div>
          <div className="flex items-center gap-1.5"><BadgeMedal tier="gold" size={18} tooltip="Gold: 90%+ over 5 attempts" /><span>90%</span></div>
        </div>
        {anyBadges ? (
          <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }} data-testid="flash-badges-counts">
            You&apos;ve earned{" "}
            {totalGold > 0 && <><b style={{ color: BADGE_STYLES.gold.ring }}>{totalGold} gold</b>{(totalSilver + totalBronze) > 0 ? " · " : ""}</>}
            {totalSilver > 0 && <><b style={{ color: BADGE_STYLES.silver.ring }}>{totalSilver} silver</b>{totalBronze > 0 ? " · " : ""}</>}
            {totalBronze > 0 && <b style={{ color: BADGE_STYLES.bronze.ring }}>{totalBronze} bronze</b>}
          </div>
        ) : (
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>
            Answer 5+ questions in any category to start earning badges.
          </div>
        )}
      </div>

      <button
        onClick={onPickSmart}
        className="pm-card pm-card-hover w-full text-left p-5 flex items-center gap-4 mb-6"
        data-testid="flash-pick-smart"
      >
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: "var(--primary)" }}
        >
          <Sparkles size={20} color="white" />
        </div>
        <div className="flex-1">
          <div className="font-display font-bold text-lg">Smart deck</div>
          <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
            20 cards mixing your weakest topics + due reviews. Best for daily study.
          </div>
        </div>
        <ChevronRight size={18} style={{ color: "var(--text-muted)" }} />
      </button>

      <h2 className="font-display font-bold text-xl mb-3">Browse by subject</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {catalog.map((s) => (
          <button
            key={s.id}
            onClick={() => onPickSubject(s)}
            disabled={!s.count}
            className="pm-card pm-card-hover p-4 text-left disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`flash-pick-subject-${s.id}`}
          >
            <div className="flex items-center justify-between mb-2">
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center"
                style={{ background: SUBJECT_ACCENTS[s.id] || "var(--primary)" }}
              >
                <Layers size={14} color="white" />
              </div>
              <div className="flex items-center gap-2">
                {s.best_badge && <BadgeMedal tier={s.best_badge} size={20} tooltip={`Best in ${s.name}: ${BADGE_STYLES[s.best_badge].label}`} />}
                <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>
                  {s.count}
                </span>
              </div>
            </div>
            <div className="font-display font-bold">{s.name}</div>
            <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
              {s.categories.length} {s.categories.length === 1 ? "category" : "categories"}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function PickerCategories({ subject, onPickAll, onPickCategory, onBack, onGenerateMore }) {
  return (
    <div data-testid="flash-picker-categories">
      <button
        onClick={onBack}
        className="text-sm inline-flex items-center gap-1 mb-4 hover:underline"
        style={{ color: "var(--primary)" }}
        data-testid="flash-picker-back-subjects"
      >
        <ArrowLeft size={14} /> All subjects
      </button>
      <h1 className="font-display text-3xl font-bold mb-1">{subject.name}</h1>
      <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>
        {subject.count} topics across {subject.categories.length} {subject.categories.length === 1 ? "category" : "categories"}.
      </p>

      <button
        onClick={onPickAll}
        className="pm-card pm-card-hover w-full text-left p-4 flex items-center gap-3 mb-5"
        data-testid="flash-pick-subject-all"
      >
        <div
          className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: SUBJECT_ACCENTS[subject.id] || "var(--primary)" }}
        >
          <Sparkles size={16} color="white" />
        </div>
        <div className="flex-1">
          <div className="font-display font-bold">All of {subject.name}</div>
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Mixed deck across every category.
          </div>
        </div>
        <ChevronRight size={16} style={{ color: "var(--text-muted)" }} />
      </button>

      <div className="grid md:grid-cols-2 gap-3">
        {subject.categories.map((c) => (
          <div
            key={c.name}
            className="pm-card p-4 text-left flex items-center justify-between gap-3 relative"
            data-testid={`flash-pick-cat-${c.name.replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase()}`}
          >
            <button
              onClick={() => onPickCategory(c)}
              className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
              title={`Open ${c.name} flashcards`}
            >
              <div className="font-display font-bold text-sm truncate">{c.name}</div>
              <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                {c.count} {c.count === 1 ? "topic" : "topics"}
                {c.attempts > 0 && (
                  <> · {Math.round(c.accuracy * 100)}% over {c.attempts}</>
                )}
              </div>
            </button>
            <div className="flex items-center gap-2 shrink-0">
              {c.badge && (
                <BadgeMedal
                  tier={c.badge}
                  size={26}
                  tooltip={`${BADGE_STYLES[c.badge].label} mastery — ${Math.round(c.accuracy * 100)}% over ${c.attempts} attempts`}
                />
              )}
              <button
                onClick={(e) => { e.stopPropagation(); onGenerateMore(subject, c); }}
                className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full transition-colors"
                style={{ background: "var(--paper)", color: "var(--primary)", border: "1.5px solid var(--primary)" }}
                data-testid={`flash-generate-more-${c.name.replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase()}`}
                title="AI-generate 5 fresh flashcards for this section (Pro · 0.25 credit)"
              >
                <Plus size={11} /> Generate more
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PickerTopics({ subject, category, onPickAll, onPickTopic, onBack, savedDecks }) {
  const topics = category.topics || [];
  return (
    <div data-testid="flash-picker-topics">
      <button
        onClick={onBack}
        className="text-sm inline-flex items-center gap-1 mb-4 hover:underline"
        style={{ color: "var(--primary)" }}
        data-testid="flash-picker-back-categories"
      >
        <ArrowLeft size={14} /> All categories
      </button>
      <div className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--text-muted)" }}>
        {subject.name}
      </div>
      <h1 className="font-display text-2xl md:text-3xl font-bold mb-1">{category.name}</h1>
      <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>
        {topics.length} {topics.length === 1 ? "topic" : "topics"} · pick one to open its AAMC term & definition set.
      </p>

      <button
        onClick={onPickAll}
        className="pm-card pm-card-hover w-full text-left p-4 flex items-center gap-3 mb-5"
        data-testid="flash-pick-cat-all"
      >
        <div
          className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: SUBJECT_ACCENTS[subject.id] || "var(--primary)" }}
        >
          <FolderTree size={16} color="white" />
        </div>
        <div className="flex-1">
          <div className="font-display font-bold">All of this category</div>
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Mixed deck across every topic in {category.name}.
          </div>
        </div>
        <ChevronRight size={16} style={{ color: "var(--text-muted)" }} />
      </button>

      <div className="grid md:grid-cols-2 gap-2">
        {topics.map((t) => (
          <button
            key={t.id}
            onClick={() => onPickTopic(t)}
            className="pm-card pm-card-hover p-3 text-left flex items-center gap-3"
            data-testid={`flash-pick-topic-${t.id}`}
          >
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: t.has_content ? "var(--hl-green)" : "var(--muted)" }}
            >
              <FileText size={14} style={{ color: t.has_content ? "var(--primary)" : "var(--text-muted)" }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{t.name}</div>
              <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                {savedDecks?.[t.id]
                  ? <span style={{ color: "var(--mastery)" }} data-testid={`flash-topic-saved-${t.id}`}>Saved set · {savedDecks[t.id]} cards · free to reopen</span>
                  : "No set yet · 0.5 credit to generate"}
              </div>
            </div>
            <ChevronRight size={14} style={{ color: "var(--text-muted)" }} />
          </button>
        ))}
      </div>
    </div>
  );
}

// --- Style variant picker -------------------------------------------------
const STYLE_SWATCH = {
  editorial: "linear-gradient(135deg, #d4885a 0%, #4f7942 100%)",
  isometric: "linear-gradient(135deg, #4f7d8a 0%, #d4a537 100%)",
  watercolor: "linear-gradient(135deg, #a5c99a 0%, #e8a595 100%)",
  "retro-diagram": "linear-gradient(135deg, #b8875e 0%, #6a4a2f 100%)",
};

function StyleChips({ styles, activeStyle, generatedStyles, disabled, onPick }) {
  if (!styles || styles.length === 0) return null;
  // Defensively normalize: backend contract is Array<string>, but during transient
  // states (e.g. auto-fill response) we may receive a dict `{styleId: dataUrl}` —
  // fall back to Object.keys in that case so `new Set(...)` never throws.
  const listOfStyles = Array.isArray(generatedStyles)
    ? generatedStyles
    : (generatedStyles && typeof generatedStyles === "object")
      ? Object.keys(generatedStyles)
      : [];
  const generatedSet = new Set(listOfStyles);
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="flash-style-chips" onClick={(e) => e.stopPropagation()}>
      {styles.map((s) => {
        const isActive = s.id === activeStyle;
        const isCached = generatedSet.has(s.id);
        return (
          <button
            key={s.id}
            onClick={() => !isActive && !disabled && onPick(s.id)}
            disabled={disabled || isActive}
            title={isActive ? `Current style: ${s.label}` : isCached ? `Switch to ${s.label} (cached)` : `Generate ${s.label} variant`}
            className="rounded-full text-[11px] font-medium inline-flex items-center gap-1.5 px-2.5 py-1 transition-transform hover:-translate-y-0.5 disabled:opacity-90 disabled:cursor-default"
            style={{
              background: "var(--paper)",
              border: `1.5px solid ${isActive ? "var(--primary)" : "var(--border)"}`,
              color: isActive ? "var(--primary)" : "var(--text-secondary)",
              boxShadow: isActive ? "0 0 0 2px rgba(79,121,66,0.15)" : "none",
            }}
            data-testid={`flash-style-chip-${s.id}`}
          >
            <span
              className="w-3 h-3 rounded-full inline-block"
              style={{ background: STYLE_SWATCH[s.id] || "var(--muted)" }}
            />
            {s.label}
            {isCached && !isActive && (
              <span className="text-[9px] opacity-60">·saved</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// --- Main page -----------------------------------------------------------

export default function Flashcards() {
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkTopicId = searchParams.get("topic_id");

  const [catalog, setCatalog] = useState([]);
  const [styleCatalog, setStyleCatalog] = useState({ default: "editorial", styles: [] });
  const [pickerLevel, setPickerLevel] = useState("landing"); // landing | categories | topics | studying
  const [pickedSubject, setPickedSubject] = useState(null);
  const [aiTool, setAiTool] = useState(null); // { type, defaults } for CoachToolModal
  const [pickedCategory, setPickedCategory] = useState(null);
  const [deckLabel, setDeckLabel] = useState("Smart deck");
  const [termTopic, setTermTopic] = useState(null);
  const [savedDecks, setSavedDecks] = useState({});
  const loadSavedDecks = () => api.get("/term-decks").then((r) => {
    setSavedDecks(Object.fromEntries((r.data.decks || []).filter((d) => d.card_count > 0).map((d) => [d.topic_id, d.card_count])));
  }).catch(() => {});

  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  const [imgError, setImgError] = useState(null);
  const [done, setDone] = useState(false);
  // ELI5 view state — per-card so switching cards resets it
  const [eli5Mode, setEli5Mode] = useState(false);
  const [eli5Loading, setEli5Loading] = useState(false);
  const [eli5Error, setEli5Error] = useState(null);

  // Load subject + style catalogs once on mount
  useEffect(() => {
    api.get("/flashcards/catalog").then((r) => setCatalog(r.data.subjects || []));
    loadSavedDecks();
    api.get("/image-styles").then((r) => setStyleCatalog(r.data || { default: "editorial", styles: [] })).catch(() => {});
  }, []);

  const startDeck = async ({ subject_id, category, topic_id, label }) => {
    const params = { limit: 20 };
    if (topic_id) params.topic_id = topic_id;
    if (subject_id) params.subject_id = subject_id;
    if (category) params.category = category;
    const r = await api.get("/flashcards", { params });
    setCards(r.data.cards || []);
    setIdx(0); setFlipped(false); setDone(false); setImgError(null);
    setDeckLabel(label);
    setPickerLevel("studying");
  };

  // Deep-link: if URL has ?topic_id=X, auto-start a 1-card deck for that topic.
  useEffect(() => {
    if (!deepLinkTopicId) return;
    (async () => {
      try {
        const r = await api.get("/flashcards", { params: { topic_id: deepLinkTopicId, limit: 1 } });
        const c = (r.data.cards || [])[0];
        if (c) {
          setCards([c]);
          setIdx(0); setFlipped(false); setDone(false); setImgError(null);
          setDeckLabel(c.name);
          setPickerLevel("studying");
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTopicId]);

  const card = cards[idx];

  const rate = async (rating) => {
    if (!card || !card.topic_id) return;
    await api.post("/flashcards/rate", { topic_id: card.topic_id, rating });
    setFlipped(false); setImgError(null);
    setEli5Mode(false); setEli5Error(null);
    if (idx + 1 >= cards.length) setDone(true);
    else setIdx(idx + 1);
  };

  const toggleEli5 = async () => {
    if (!card) return;
    // Toggle off if already showing ELI5
    if (eli5Mode) { setEli5Mode(false); return; }
    // If we already have cached eli5, just show it
    if (card.eli5) { setEli5Mode(true); return; }
    if (!card.topic_id) {
      setEli5Error("Card is still loading — flip back and try again in a sec.");
      return;
    }
    // Otherwise fetch from backend
    setEli5Loading(true);
    setEli5Error(null);
    try {
      const r = await api.post(`/topics/${card.topic_id}/generate-eli5`);
      const text = r?.data?.eli5;
      if (!text) {
        setEli5Error("Couldn't simplify that. Try again?");
        return;
      }
      const updated = [...cards];
      updated[idx] = { ...card, eli5: text };
      setCards(updated);
      setEli5Mode(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "ELI5 failed.";
      setEli5Error(typeof detail === "string" ? detail : "ELI5 failed. Try again?");
    } finally {
      setEli5Loading(false);
    }
  };

  const genImage = async (opts = {}) => {
    const { force = false, style } = opts;
    if (!card) return;
    if (!card.topic_id) {
      setImgError("Card is still loading — try again in a sec.");
      return;
    }
    setImgLoading(true);
    setImgError(null);
    const attempt = async () => {
      const params = new URLSearchParams();
      if (force) params.set("force", "true");
      if (style) params.set("style", style);
      const qs = params.toString() ? `?${params.toString()}` : "";
      return api.post(`/topics/${card.topic_id}/generate-image${qs}`);
    };
    try {
      let r;
      try {
        r = await attempt();
      } catch (firstErr) {
        // Auto-retry once after 800ms — most Nano Banana failures are transient.
        await new Promise((res) => setTimeout(res, 800));
        r = await attempt();
      }
      if (!r?.data?.image_url) {
        setImgError("The illustrator drew a blank. Try again?");
        return;
      }
      const updated = [...cards];
      updated[idx] = {
        ...card,
        image_url: r.data.image_url,
        image_style: r.data.style || style || card.image_style,
        image_variants: r.data.variants || card.image_variants || [],
      };
      setCards(updated);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Something went wrong.";
      setImgError(typeof detail === "string" ? detail : "Image generation failed after 2 tries. Try again?");
    } finally { setImgLoading(false); }
  };

  // Note: previously we had a separate auto-image useEffect that fired on flip
  // when a card had text but no image. That created a two-step visual (text
  // appears → then a spinner where the image should be). We've folded that
  // behavior into the unified FlashcardGenerator flow below, which handles
  // both content + image in one atomic call to /generate-content.

  const returnToPicker = () => {
    // Clear deep-link so we don't re-auto-start.
    if (deepLinkTopicId) {
      setSearchParams({}, { replace: true });
    }
    // From studying, go back to whichever level we came from.
    if (pickedCategory) setPickerLevel("topics");
    else if (pickedSubject) setPickerLevel("categories");
    else setPickerLevel("landing");
    setCards([]); setIdx(0); setFlipped(false); setDone(false); setImgError(null);
  };

  // ---- Render: Picker landing ----
  if (pickerLevel === "landing") {
    return (
      <div className="max-w-4xl mx-auto">
        <PickerLanding
          catalog={catalog}
          onPickSmart={() => startDeck({ label: "Smart deck" })}
          onPickSubject={(s) => { setPickedSubject(s); setPickedCategory(null); setPickerLevel("categories"); }}
        />
      </div>
    );
  }

  // ---- Render: Picker categories ----
  if (pickerLevel === "categories" && pickedSubject) {
    return (
      <div className="max-w-4xl mx-auto">
        <PickerCategories
          subject={pickedSubject}
          onBack={() => { setPickerLevel("landing"); setPickedSubject(null); setPickedCategory(null); }}
          onPickAll={() => startDeck({ subject_id: pickedSubject.id, label: `${pickedSubject.name} — all` })}
          onPickCategory={(c) => {
            setPickedCategory(c);
            setPickerLevel("topics");
          }}
          onGenerateMore={(subj, cat) => setAiTool({
            type: "quiz",
            defaults: {
              subject: subj.name,
              topic: cat.name,
              difficulty: "medium",
              count: 5,
              use_cache: true,
            },
          })}
        />
        {aiTool && (
          <CoachToolModal
            open={!!aiTool}
            onClose={() => setAiTool(null)}
            type={aiTool.type}
            defaults={aiTool.defaults}
          />
        )}
      </div>
    );
  }

  // ---- Render: Picker topics (inside a category) ----
  if (pickerLevel === "topics" && pickedSubject && pickedCategory) {
    return (
      <div className="max-w-4xl mx-auto">
        <PickerTopics
          subject={pickedSubject}
          category={pickedCategory}
          onBack={() => { setPickerLevel("categories"); setPickedCategory(null); }}
          onPickAll={() => startDeck({
            subject_id: pickedSubject.id,
            category: pickedCategory.name,
            label: pickedCategory.name,
          })}
          onPickTopic={(t) => { setTermTopic(t); setPickerLevel("term-deck"); }}
          savedDecks={savedDecks}
        />
      </div>
    );
  }

  // ---- Render: AAMC term/definition set for one topic ----
  if (pickerLevel === "term-deck" && termTopic) {
    return (
      <TermDeck
        topic={termTopic}
        onBack={() => { loadSavedDecks(); setTermTopic(null); setPickerLevel(pickedCategory ? "topics" : "landing"); }}
        onOpenAnalogy={() => startDeck({ topic_id: termTopic.id, label: termTopic.name })}
      />
    );
  }

  // ---- Render: Empty deck ----
  if (pickerLevel === "studying" && cards.length === 0 && !done) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16" data-testid="flash-empty">
        <div className="text-4xl mb-3">📭</div>
        <p className="text-lg" style={{ color: "var(--text-secondary)" }}>
          No cards ready in this deck yet — open some topics first to seed them.
        </p>
        <button onClick={returnToPicker} className="pm-btn-primary mt-6" data-testid="flash-back-picker">
          Back to picker
        </button>
      </div>
    );
  }

  // ---- Render: Deck complete ----
  if (done) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16" data-testid="flash-done">
        <div className="text-6xl mb-4">🃏</div>
        <h2 className="font-display text-3xl font-bold mb-3">Deck complete!</h2>
        <p className="text-lg mb-6" style={{ color: "var(--text-secondary)" }}>
          You reviewed {cards.length} cards in <b>{deckLabel}</b>. Your streak just got longer.
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button onClick={returnToPicker} className="pm-btn-secondary" data-testid="flash-back-picker">
            Pick another deck
          </button>
          <button
            onClick={() => startDeck({
              subject_id: pickedSubject?.id,
              category: pickedCategory?.name,
              label: deckLabel,
            })}
            className="pm-btn-primary"
            data-testid="flash-restart"
          >
            Restart this deck
          </button>
        </div>
      </div>
    );
  }

  // ---- Render: Studying (card view) ----
  return (
    <div className="max-w-2xl mx-auto" data-testid="flashcards-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={returnToPicker}
            className="text-xs inline-flex items-center gap-1 mb-1 hover:underline"
            style={{ color: "var(--primary)" }}
            data-testid="flash-back-picker"
          >
            <ArrowLeft size={12} /> Change deck
          </button>
          <h1 className="font-display text-3xl font-bold">{deckLabel}</h1>
        </div>
        <div className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="flash-progress">
          Card {idx + 1} of {cards.length}
        </div>
      </div>

      <div
        onClick={() => setFlipped(!flipped)}
        className="pm-card p-8 cursor-pointer select-none min-h-[420px] flex flex-col justify-center items-center text-center relative"
        data-testid="flash-card"
        style={{ background: flipped ? "var(--hl-yellow)" : "var(--paper)" }}
      >
        {!flipped ? (
          <>
            <div className="pm-badge mb-6" style={{ background: "var(--muted)", color: "var(--text-muted)" }}>
              {card.category || "Topic"}
            </div>
            <div className="font-display text-3xl md:text-4xl font-bold mb-4" data-testid="flash-topic-name">{card.name}</div>
            {(card.needs_content || (!card.image_url && !card.gen_attempted)) && (
              <div className="text-xs mt-2 mb-3 pm-badge" style={{ background: "var(--hl-yellow)" }}>Tap flip to auto-generate this card</div>
            )}
            <div className="text-sm mt-6" style={{ color: "var(--text-muted)" }}>
              <RotateCw size={14} className="inline mr-1" /> Tap card to flip
            </div>
          </>
        ) : (card.needs_content || (!card.image_url && !card.gen_attempted)) ? (
          <FlashcardGenerator card={card} onFilled={(updated) => {
            const copy = [...cards];
            // Merge the generated payload onto the existing card, preserving
            // fields the backend response doesn't include (notably `topic_id`
            // — /generate-content returns `id`, so a naive spread would drop
            // `topic_id` and every follow-up call would hit /topics/undefined/...).
            copy[idx] = {
              ...card,
              ...updated,
              topic_id: card.topic_id || updated.id,
              needs_content: false,
              // Mark that we've attempted a full generation for this card so
              // we don't re-trigger FlashcardGenerator on subsequent flips if
              // image gen silently failed (user can still tap "Generate image").
              gen_attempted: true,
            };
            setCards(copy);
          }} />
        ) : (
          <div className="w-full text-left" data-testid="flash-back">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div
                className="pm-badge"
                style={{ background: eli5Mode ? "var(--primary)" : "var(--accent)", color: "white" }}
                data-testid="flash-mode-badge"
              >
                {eli5Mode ? "ELI5" : "Analogy"}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); toggleEli5(); }}
                disabled={eli5Loading}
                className="text-xs inline-flex items-center gap-1.5 rounded-full px-3 py-1 transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                style={{
                  background: eli5Mode ? "var(--primary)" : "var(--paper)",
                  color: eli5Mode ? "white" : "var(--text-secondary)",
                  border: `1.5px solid ${eli5Mode ? "var(--primary)" : "var(--border)"}`,
                }}
                data-testid="flash-eli5-toggle"
                title={eli5Mode ? "Switch back to grown-up analogy" : "Explain like I'm 5"}
                aria-pressed={eli5Mode}
              >
                {eli5Loading ? (
                  <><Loader2 size={12} className="animate-spin" /> Simplifying…</>
                ) : eli5Mode ? (
                  <><GraduationCap size={12} /> Grown-up view</>
                ) : (
                  <><Baby size={12} /> Explain like I&apos;m 5</>
                )}
              </button>
            </div>
            {eli5Mode ? (
              <div
                className="font-display text-base leading-relaxed mb-4 whitespace-pre-wrap"
                style={{ color: "var(--text-primary)" }}
                data-testid="flash-eli5-text"
              >
                {card.eli5}
              </div>
            ) : (
              <div className="font-display text-xl leading-snug mb-4" data-testid="flash-analogy-text">{card.analogy}</div>
            )}
            {eli5Error && !eli5Mode && (
              <div
                className="text-xs mb-3"
                style={{ color: "var(--weakness)" }}
                data-testid="flash-eli5-error"
              >
                {eli5Error}
              </div>
            )}
            {card.image_url ? (
              <div className="mb-4">
                <img
                  src={card.image_url}
                  alt={card.name}
                  loading="lazy"
                  className="w-full rounded-xl max-h-56 object-cover"
                  data-testid="flash-image"
                />
                <StyleChips
                  styles={styleCatalog.styles}
                  activeStyle={card.image_style || styleCatalog.default}
                  generatedStyles={card.image_variants || []}
                  disabled={imgLoading}
                  onPick={(sid) => genImage({ style: sid })}
                />
                <button
                  onClick={(e) => { e.stopPropagation(); genImage({ force: true, style: card.image_style }); }}
                  disabled={imgLoading}
                  className="text-xs mt-1 hover:underline"
                  style={{ color: "var(--text-muted)" }}
                  data-testid="flash-regen-image-btn"
                >
                  {imgLoading ? "Regenerating…" : "↻ Regenerate this style"}
                </button>
              </div>
            ) : imgLoading ? (
              <div className="mb-4 flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }} data-testid="flash-image-loading">
                <Loader2 size={14} className="animate-spin" />
                Painting an analogy…
              </div>
            ) : (
              <div className="mb-4" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => genImage()}
                  disabled={imgLoading}
                  className="pm-btn-secondary text-xs inline-flex items-center gap-2"
                  data-testid="flash-gen-image-btn"
                >
                  {imgLoading ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
                  {imgLoading ? "Generating…" : imgError ? "Try again" : "Generate image"}
                </button>
                {imgError && (
                  <div
                    className="text-xs mt-2"
                    style={{ color: "var(--weakness)" }}
                    data-testid="flash-image-error"
                  >
                    {imgError}
                  </div>
                )}
              </div>
            )}
            {card.story && <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>{card.story}</p>}
            {card.key_points?.length > 0 && (
              <ul className="text-sm space-y-1">
                {card.key_points.map((p, i) => (
                  <li key={i} className="flex gap-2"><Check size={14} className="mt-1 shrink-0" style={{ color: "var(--mastery)" }} /><span>{p}</span></li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {flipped && (
        <div className="grid grid-cols-4 gap-2 mt-5" data-testid="flash-ratings">
          {RATINGS.map((r) => (
            <button
              key={r.id}
              onClick={() => rate(r.id)}
              className="rounded-2xl py-3 flex flex-col items-center transition-transform hover:-translate-y-1"
              style={{ background: r.color, color: "white" }}
              data-testid={`flash-rate-${r.id}`}
            >
              <span className="font-medium">{r.label}</span>
              <span className="text-xs opacity-80 mt-0.5">{r.desc}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
